import type { OpsTask, ResosysTourBooking } from "@getaways/tours";
import { api, Badge, Banner, dateTime, Empty, go, Metric, PageHead, useAsync } from "@getaways/extranet-ui";
import type { Overview } from "../App";
import { amount, localDate } from "../lib/format";
import { lead, MODES, STATUS, TASKS } from "../lib/labels";

export function OverviewPage({ overview }: { overview: Overview | undefined }) {
  const recent = useAsync(() => api<{ bookings: ResosysTourBooking[] }>("GET", "/v1/admin/bookings?limit=8"), []);
  const tasks = useAsync(() => api<{ tasks: OpsTask[] }>("GET", "/v1/admin/tasks?limit=8"), []);
  const mode = overview ? MODES[overview.mode] : undefined;

  return (
    <>
      <PageHead title="Overview" sub={mode ? <Badge tone={mode[0]}>{mode[1]}</Badge> : undefined} />
      {overview && overview.bookableSuppliers.length === 0 && <Banner notice={{ tone: "warn", text: "No supplier is open for booking yet. Check the settings and open it in Suppliers and markets." }} />}
      <div className="metrics">
        <Metric label="Confirmed bookings" value={overview?.confirmedBookings ?? "—"} />
        <Metric label="Held now" value={overview?.heldBookings ?? "—"} hint="Spaces held with the supplier, not yet confirmed" />
        <Metric label="Cancellations pending" value={overview?.cancellationsPending ?? "—"} tone={overview?.cancellationsPending ? "warn" : undefined} />
        <Metric label="Open tasks" value={overview?.openTasks ?? "—"} tone={overview?.openTasks ? "warn" : undefined} />
        <Metric label="Tours in the catalogue" value={overview?.tours ?? "—"} />
        <Metric label="Reconciliation issues" value={overview?.openReconciliationIssues ?? "—"} tone={overview?.openReconciliationIssues ? "warn" : undefined} />
      </div>
      <div className="stack">
        <div className="card">
          <h2>Tasks</h2>
          {tasks.data && tasks.data.tasks.length === 0 && <Empty>Nothing to do.</Empty>}
          {(tasks.data?.tasks ?? []).map((t) => (
            <div className="list-row click" key={t.id} role="link" tabIndex={0} onClick={() => (t.bookingId ? go("bookings", t.bookingId) : go("tasks"))}>
              <div style={{ flex: 1, minWidth: 0 }}><div>{t.summary}</div><div className="small muted">{TASKS[t.kind] ?? t.kind}{t.dueAt ? ` · due ${dateTime(t.dueAt)}` : ""}</div></div>
            </div>
          ))}
        </div>
        <div className="card">
          <h2>Latest bookings</h2>
          {recent.data && recent.data.bookings.length === 0 && <Empty>No bookings yet. Try Sell a tour.</Empty>}
          {(recent.data?.bookings ?? []).map((b) => (
            <div className="list-row click" key={b.id} role="link" tabIndex={0} onClick={() => go("bookings", b.id)} onKeyDown={(e) => e.key === "Enter" && go("bookings", b.id)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div>{b.tour.name}</div>
                <div className="small muted">{b.clientReference} · {lead(b) ? `${lead(b)!.firstName} ${lead(b)!.lastName}` : "—"} · starts {localDate(b.departure.start)}</div>
              </div>
              <span className="small">{amount(b.sell)}</span>
              <Badge tone={STATUS[b.status][0]}>{STATUS[b.status][1]}</Badge>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
